import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, MenuItem, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { batchUpdateOrganisations, getFirmUsers } from '../rest/BackendApi';
import Loading from './Loading';
import 'react-quill-new/dist/quill.snow.css';
import 'react-quill-new/dist/quill.bubble.css';
import { FirmUser } from '../types/FirmType';
import { PeriodEnum } from '../model/PeriodEnum';
import { getFullName } from '../util/DataUtil';
import { Organisation } from '../types/OrganisationType';
import { plural } from '../util/StringUtil';
import UserContext from '../UserContext';
import { hasFeature } from '../business/Subscription';

export type BatchUpdateOrganisationDialogProps = {
  open: boolean;
  onClose?: (arg0: BatchUpdateOrganisationResult | undefined) => void;
  orgIds?: number[];
  firmId?: number;
};

export type BatchUpdateOrganisationResult = {
  error?: string;
  organisations?: Organisation[];
};

export type BatchUpdateOrganisationSelectedData = {
  clientManagerId?: number;
  tag?: string;
  period?: PeriodEnum;
  autoReminder?: boolean;
};

const BlurryDialog = styled(Dialog)<DialogProps>(({ theme }) => ({
  backdropFilter: 'blur(2px)',
}));

const BatchUpdateOrganisationDialog = (props: BatchUpdateOrganisationDialogProps) => {
  const { orgIds, firmId, open, onClose } = props;
  const user = useContext(UserContext)?.user;
  const [loading, setLoading] = useState<boolean>(true);
  const [clientManagers, setClientManagers] = useState<FirmUser[]>([]);
  const [data, setData] = useState<BatchUpdateOrganisationSelectedData | undefined>(undefined);

  useEffect(() => {
    if (orgIds && firmId) {
      setLoading(true);
      getFirmUsers(firmId)
        .then(response => {
          setClientManagers(response);
          setLoading(false);
        })
        .catch(reason => {
          close({ error: "Couldn't load client managers" });
          setLoading(false);
        });
    }
  }, [firmId]);

  function close(result?: BatchUpdateOrganisationResult) {
    onClose && onClose(result);
    setData(undefined);
  }

  function noneAsUndefined(value: string): string | undefined {
    if (value == 'none') {
      return undefined;
    }
    return value;
  }

  function toNumberOrUndefined(value: string): number | undefined {
    const parsed = noneAsUndefined(value);
    return parsed === undefined ? undefined : Number(parsed);
  }

  function toBooleanOrUndefined(value: string): boolean | undefined {
    const parsed = noneAsUndefined(value);
    if (parsed === undefined) return undefined;
    return parsed === 'true';
  }

  async function updateOrganisations() {
    if (!orgIds) {
      close();
      return;
    }
    if (data == undefined || (data.clientManagerId === undefined && data.period === undefined && data.tag === undefined && data.autoReminder === undefined)) {
      close({ error: `Need to update at least one field` });
      return;
    }
    setLoading(true);
    try {
      return await batchUpdateOrganisations({
        clientManagerId: data.clientManagerId,
        paygwPeriod: data.period,
        autoReminderBasDue: data.autoReminder,
        tag: data.tag,
        orgIds: orgIds,
      });
    } catch (error) {
      close({ error: 'Unexpected error: ' + error });
    } finally {
      setLoading(false);
    }
  }

  if (!open) {
    return;
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <BlurryDialog transitionDuration={0} hideBackdrop={true} open={open} onClose={() => close()} aria-labelledby="confirm-dialog" fullWidth maxWidth="xs">
      <DialogTitle id="confirm-dialog" sx={{ fontWeight: 600 }}>
        Update {plural(orgIds?.length || 0, 'organisation')}
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Only the fields you change are updated. Everything else is left as it is.
        </Typography>

        <Box sx={{ mt: '5px', '& .MuiInputBase-root': { fontSize: '0.95rem' } }} display="flex" flexDirection="column" component="form" gap={2}>
          <TextField
            select
            size="small"
            value={data?.clientManagerId === undefined ? 'none' : String(data.clientManagerId)}
            onChange={e => setData({ ...data, clientManagerId: toNumberOrUndefined(e.target.value) })}
            label={'Client manager'}
          >
            <MenuItem value="none">
              <em style={{ color: 'gray' }}>Keep assigned manager</em>
            </MenuItem>
            {clientManagers.map((clientManager, index) => {
              return (
                <MenuItem key={clientManager.id} value={clientManager.id}>
                  {getFullName(clientManager)}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            select
            size="small"
            value={data?.period || 'none'}
            onChange={e => setData({ ...data, period: noneAsUndefined(e.target.value) as PeriodEnum })}
            label={'PAYGW period'}
          >
            <MenuItem value="none">
              <em style={{ color: 'gray' }}>Keep assigned period</em>
            </MenuItem>
            <MenuItem value={'MONTHLY'}>{PeriodEnum.MONTHLY}</MenuItem>
            <MenuItem value={'QUARTERLY'}>{PeriodEnum.QUARTERLY}</MenuItem>
          </TextField>
          {hasFeature(user, 'Emails') && (
            <TextField
              select
              size="small"
              value={data?.autoReminder === undefined ? 'none' : String(data.autoReminder)}
              onChange={e => setData({ ...data, autoReminder: toBooleanOrUndefined(e.target.value) })}
              label={'Auto reminder emails'}
            >
              <MenuItem value="none">
                <em style={{ color: 'gray' }}>Keep setting</em>
              </MenuItem>
              <MenuItem value={'true'}>Enabled</MenuItem>
              <MenuItem value={'false'}>Disabled</MenuItem>
            </TextField>
          )}
          <TextField
            size="small"
            InputLabelProps={{ shrink: true }}
            onChange={e => setData({ ...data, tag: e.target.value || undefined })}
            value={data?.tag || ''}
            label="Add tag"
            placeholder="e.g. late"
            helperText="Added to existing tags. Leave empty to keep tags as they are."
            inputProps={{ maxLength: 32 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => close()} color="inherit">
          Cancel
        </Button>

        <Button
          disabled={data?.clientManagerId == undefined && data?.tag == undefined && data?.period == undefined && data?.autoReminder == undefined}
          variant="contained"
          onClick={async () => {
            const organisations = await updateOrganisations();
            // On failure updateOrganisations has already closed the dialog with the error
            if (organisations) {
              close({ organisations });
            }
          }}
        >
          Update {plural(orgIds?.length || 0, 'organisation')}
        </Button>
      </DialogActions>
    </BlurryDialog>
  );
};
export default BatchUpdateOrganisationDialog;
